/**
 * useLocalStorage Hook
 * 
 * Syncs React state with localStorage.
 */

import { useCallback,useEffect, useState } from 'react';

import { getStorageItem, setStorageItem } from '@/lib/storageUtils';

/**
 * Hook to persist state in localStorage.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  options?: {
    legacyKeys?: string[];
  }
): [T, (value: T | ((prev: T) => T)) => void] {
  // Get initial value from localStorage or use provided initial value
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const existing = localStorage.getItem(key);
      if (existing != null) return getStorageItem<T>(key, initialValue);

      const legacyKeys = options?.legacyKeys ?? [];
      for (const legacyKey of legacyKeys) {
        const legacyExisting = localStorage.getItem(legacyKey);
        if (legacyExisting == null) continue;

        const legacyValue = getStorageItem<T>(legacyKey, initialValue);
        // Migrate forward so future reads use the canonical key.
        setStorageItem(key, legacyValue);
        return legacyValue;
      }
    } catch {
      // Ignore and fall back to initialValue below.
    }

    return getStorageItem<T>(key, initialValue);
  });

  // Update localStorage when state changes
  useEffect(() => {
    setStorageItem(key, storedValue);
  }, [key, storedValue]);

  // Listen for changes from other tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.newValue) {
        try {
          setStoredValue(JSON.parse(event.newValue) as T);
        } catch {
          // Ignore parse errors
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  // Setter function with support for updater function
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStoredValue((prev: T) => {
        const newValue = value instanceof Function ? value(prev) : value;
        return newValue;
      });
    },
    []
  );

  return [storedValue, setValue];
}

